import { useState, useEffect, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";

import Avatar from "../core/Avatar";
import ComboBox from "../core/ComboBox";
import { useAppData } from "../../context/AppDataContext";
import { useAuthInfo } from "../../context/AuthContext";
import { errorToast, successfulToast } from "../../util/toastNotifications";
import { resolveTaskAssigneeOrgId } from "../../util/resolveTaskAssigneeOrgId";

const TaskAssignments = ({
  taskId,
  assignments = [],
  onAssignmentsChange,
  readOnly = false,
  layout = null,
  task,
  projects = [],
}) => {
  const [showComboBox, setShowComboBox] = useState(false);
  const [users, setUsers] = useState([]);
  const [tooltip, setTooltip] = useState({
    show: false,
    text: "",
    position: { x: 0, y: 0 },
  });

  const [isOverflowOpen, setIsOverflowOpen] = useState(false);

  const { api } = useAppData();
  const { userInfo } = useAuthInfo();
  const containerRef = useRef(null);

  const assigneeOrgId = useMemo(() => {
    const resolved = resolveTaskAssigneeOrgId(task, projects);
    if (resolved) return resolved;
    return userInfo?.org_id ?? null;
  }, [task, projects, userInfo?.org_id]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (!assigneeOrgId) {
          setUsers([]);
          return;
        }
        const usersData = await api.user.getUsersByOrganization(assigneeOrgId);
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
        errorToast("Failed to load users");
      }
    };

    if (showComboBox && assigneeOrgId) {
      fetchUsers();
    }
  }, [api, showComboBox, assigneeOrgId]);

  const handleAssignmentToggle = async (userId) => {
    try {
      const currentUserIds = assignments.map(
        (assignment) => assignment.user_id
      );
      let newUserIds;

      if (currentUserIds.includes(userId)) {
        newUserIds = currentUserIds.filter((id) => id !== userId);
      } else {
        newUserIds = [...currentUserIds, userId];
      }
      await api.task.assignUsers(taskId, {
        user_ids: newUserIds,
        ...(task?.version && { version: task.version }),
      });

      if (onAssignmentsChange) {
        const updatedAssignments = users
          .filter((user) => newUserIds.includes(user.user_id))
          .map((user) => ({
            user_id: user.user_id,
            user: user,
          }));
        onAssignmentsChange(updatedAssignments);
      }
      successfulToast("Task assignments updated");
    } catch (error) {
      if (error && error.status === 409) {
        errorToast("This task was updated by another user. Please refresh.");
      } else {
        errorToast("Failed to update task assignments");
      }
    }
  };

  const handleComboBoxClose = () => {
    setShowComboBox(false);
  };

  const formatUserData = (user) => {
    const email = user.email || "No email";
    return `${user.first_name} ${user.last_name} (${email})`;
  };

  const handleMouseEnter = (event, user) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      text:
        `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
        "Unknown User",
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      },
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, text: "", position: { x: 0, y: 0 } });
  };
  const visibleAssignments = assignments.slice(0, 2);
  const overflowAssignments = assignments.slice(2);
  const overflowCount = overflowAssignments.length;

  const renderAssignments = () => {
    return (
      <div
        className={`task-assignments-list ${layout ? "flex-align-start" : ""}`}
      >
        {
          <>
            {visibleAssignments.map((assignment, index) => (
              <div
                key={`${assignment.user_id}-${index}`}
                className="assignment-avatar"
                onMouseEnter={(e) => handleMouseEnter(e, assignment.user)}
                onMouseLeave={handleMouseLeave}
              >
                <Avatar userData={assignment.user || {}} />
              </div>
            ))}

            {assignments.length > 2 && (
              <div
                className="overflow-wrapper"
                onMouseEnter={() => setIsOverflowOpen(true)}
                onMouseLeave={() => setIsOverflowOpen(false)}
              >
                <div className="overflow-assignments">+{overflowCount}</div>
                {isOverflowOpen && (
                  <div className="overflow-assignments-box">
                    {overflowAssignments.map((assignment, idx) => (
                      <div
                        key={`${assignment.user_id}-o-${idx}`}
                        className="overflow-user"
                      >
                        <span>
                          {assignment.user.first_name}{" "}
                          {assignment.user.last_name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        }

        {!readOnly && (
          <button
            className={`assignment-add-button ${
              layout ? "flex-align-start mt-1px" : ""
            }`}
            onClick={() => setShowComboBox(true)}
            aria-label="Add another user assignment"
            type="button"
          >
            <FontAwesomeIcon icon={faUserPlus} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="task-assignments-container" ref={containerRef}>
      {renderAssignments()}

      {showComboBox && (
        <div className="assignments-combobox-wrapper">
          <ComboBox
            data={users}
            selected={assignments.map((assignment) => assignment.user)}
            valueKey="user_id"
            placeholder="Search users..."
            searchTopic="user"
            formatData={formatUserData}
            handleSelect={handleAssignmentToggle}
            onRequestClose={handleComboBoxClose}
          />
        </div>
      )}

      {tooltip.show && (
        <div
          className="assignment-tooltip"
          style={{
            position: "fixed",
            left: tooltip.position.x,
            top: tooltip.position.y,
            transform: "translateX(-50%) translateY(-100%)",
            zIndex: 10000,
          }}
        >
          {tooltip.text}
          <div className="tooltip-arrow" />
        </div>
      )}
    </div>
  );
};

TaskAssignments.propTypes = {
  taskId: PropTypes.string.isRequired,
  assignments: PropTypes.arrayOf(
    PropTypes.shape({
      user_id: PropTypes.string.isRequired,
      user: PropTypes.shape({
        user_id: PropTypes.string.isRequired,
        first_name: PropTypes.string.isRequired,
        last_name: PropTypes.string.isRequired,
        email: PropTypes.string,
        color: PropTypes.string,
        profile_avatar: PropTypes.object,
      }).isRequired,
    })
  ),
  onAssignmentsChange: PropTypes.func,
  readOnly: PropTypes.bool,
  layout: PropTypes.string,
  task: PropTypes.shape({
    task_id: PropTypes.string,
    project_id: PropTypes.string,
    org_id: PropTypes.string,
    version: PropTypes.number,
    project: PropTypes.object,
  }),
  projects: PropTypes.arrayOf(PropTypes.object),
};

export default TaskAssignments;
